import React, { useState, useEffect } from "react";
import { Loader2, X, Layers, Trophy, AlertTriangle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modo Comparativa cross-licitación: selecciona 2-4 sesiones Pando completed
 * y genera análisis comparativo + matriz de scoring + recomendación.
 */
export default function CompareLicitacionesModal({ open, onClose, onGenerated }) {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState([]); // session_ids
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState("");
  const [synthesisModel, setSynthesisModel] = useState("claude-sonnet-4-6");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/licitaciones/ai-analysis/compare/eligible-sessions`)
      .then(r => setSessions(r.data?.sessions || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
    setResult(null);
    setSelected([]);
  }, [open]);

  useEffect(() => {
    if (!running) return;
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(id);
  }, [running]);

  if (!open) return null;

  const toggleSession = (sid) => {
    setSelected(prev => {
      if (prev.includes(sid)) return prev.filter(x => x !== sid);
      if (prev.length >= 4) {
        toast.warning("Máximo 4 sesiones por comparativa");
        return prev;
      }
      return [...prev, sid];
    });
  };

  const launch = async () => {
    if (selected.length < 2) return toast.error("Selecciona al menos 2 sesiones");
    setRunning(true);
    setResult(null);
    try {
      // 1. POST encola el job (HTTP 202) → recibimos job_id
      const r = await axios.post(`${API}/licitaciones/ai-analysis/compare`, {
        session_ids: selected,
        focus: focus.trim() || undefined,
        synthesis_model: synthesisModel,
      });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("Backend no devolvió job_id");

      // 2. Polling cada 4 s a /api/ai-jobs/{job_id} hasta status==='done'
      const POLL_MS = 4000;
      const MAX_MS = 15 * 60 * 1000; // 15 min hard cap
      const t0 = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - t0 > MAX_MS) {
          throw new Error("Timeout esperando el job (15 min)");
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, POLL_MS));
        // eslint-disable-next-line no-await-in-loop
        const s = await axios.get(`${API}/ai-jobs/${jobId}`);
        const st = s.data?.status;
        if (st === "done") {
          const out = s.data.result;
          setResult(out);
          toast.success(`📊 Comparativa lista · ${out.elapsed_s}s · $${out.cost_usd}`);
          onGenerated && onGenerated(out);
          break;
        }
        if (st === "failed" || st === "cancelled") {
          throw new Error(s.data?.error || `Job ${st}`);
        }
        if (st === "not_found") {
          throw new Error("Job no encontrado");
        }
        // 'running' → seguimos esperando
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error("Error: " + (msg || e.message));
    } finally {
      setRunning(false);
    }
  };

  // Agrupar por tipo_servicio (para sugerir comparables)
  const grouped = sessions.reduce((acc, s) => {
    const k = s.lic_tipo_servicio || "(sin tipo)";
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  const ranking = result?.analysis?.ranking || [];
  const reco = result?.analysis?.recomendacion_final;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="compare-licitaciones-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <Layers size={22} />
            <div>
              <h3 className="font-bold text-base">Comparativa cross-licitación</h3>
              <p className="text-[11px] opacity-90">Selecciona 2-4 oportunidades · scoring multi-criterio + recomendación</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="compare-licitaciones-close" disabled={running}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!result ? (
            loading ? (
              <div className="text-center py-8 text-gray-500 text-sm"><Loader2 className="animate-spin inline mr-2"/>Cargando sesiones…</div>
            ) : sessions.length < 2 ? (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-[12px] text-amber-900">
                <AlertTriangle className="inline mr-1.5" size={14} />
                Necesitas al menos <b>2 sesiones Pando completadas</b> (modo Por lotes) en licitaciones distintas para comparar. Ejecuta &quot;Generar análisis Pando&quot; modo &quot;Por lotes&quot; en al menos 2 licitaciones, luego vuelve aquí.
              </div>
            ) : (
              <>
                <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-bold tracking-wide text-amber-900 mb-1.5">
                    Sesiones elegibles · {sessions.length} ({selected.length}/4 seleccionadas)
                  </div>
                  <div className="text-[10px] text-gray-600">
                    Tip: para mejor comparativa, elige licitaciones del mismo tipo de servicio.
                  </div>
                </div>

                {Object.entries(grouped).map(([tipo, sesss]) => (
                  <div key={tipo}>
                    <div className="text-[10px] uppercase font-bold tracking-wide text-gray-600 mb-1.5">
                      {tipo} ({sesss.length})
                    </div>
                    <div className="space-y-1">
                      {sesss.map(s => {
                        const sel = selected.includes(s.session_id);
                        return (
                          <label
                            key={s.session_id}
                            data-testid={`compare-session-${s.session_id}`}
                            className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition ${
                              sel ? "border-amber-500 bg-amber-50 ring-1 ring-amber-300" : "border-gray-200 hover:bg-gray-50"
                            }`}>
                            <input
                              type="checkbox"
                              checked={sel}
                              onChange={() => toggleSession(s.session_id)}
                              className="accent-amber-600"
                              data-testid={`compare-checkbox-${s.session_id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] font-semibold text-gray-900 truncate">{s.lic_titulo}</div>
                              <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                                <span>📍 {s.lic_municipio || "—"}</span>
                                <span>· {s.extracts_count} extractos</span>
                                <span>· {s.lic_estado}</span>
                                {s.lic_presupuesto > 0 && <span>· {(s.lic_presupuesto/1_000_000).toFixed(2)}M€</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {selected.length >= 2 && (
                  <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200 space-y-3" data-testid="compare-launch-panel">
                    <div>
                      <label className="text-[10px] font-bold text-gray-700 block mb-1 uppercase tracking-wide">Foco temático (opcional)</label>
                      <input
                        type="text"
                        value={focus}
                        onChange={e => setFocus(e.target.value)}
                        placeholder="ej. encaje técnico VSM, riesgos económicos, capacidad operativa…"
                        data-testid="compare-focus"
                        className="w-full text-[12px] border border-gray-300 rounded px-2 py-1.5"
                      />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase font-bold text-gray-700">Modelo</span>
                      <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 bg-white" data-testid="compare-model-selector">
                        {[
                          { v: "claude-sonnet-4-6", label: "Sonnet 4.6" },
                          { v: "claude-opus-4-8", label: "Opus 4.8" },
                        ].map(opt => (
                          <button
                            key={opt.v}
                            onClick={() => setSynthesisModel(opt.v)}
                            data-testid={`compare-model-${opt.v}`}
                            className={`text-[11px] px-2.5 py-1 font-semibold transition-colors ${
                              synthesisModel === opt.v ? "bg-orange-600 text-white" : "text-gray-700 hover:bg-orange-50"
                            }`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={launch}
                      disabled={running || selected.length < 2}
                      data-testid="compare-launch"
                      className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:opacity-90 text-white font-bold text-sm py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {running ? (
                        <><Loader2 className="animate-spin" size={16}/> Comparando {selected.length} licitaciones ({elapsed}s)…</>
                      ) : (
                        <>⚖️ Lanzar comparativa ({selected.length} licitaciones · estimado ~${(selected.length * 0.25).toFixed(2)})</>
                      )}
                    </button>
                  </div>
                )}
              </>
            )
          ) : (
            <div data-testid="compare-result" className="space-y-3">
              {reco && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="text-amber-600" size={22}/>
                    <h3 className="text-sm font-bold text-amber-900">Mejor opción para Valoriza</h3>
                  </div>
                  <div className="text-[13px] font-bold text-amber-900 mb-2">
                    {sessions.find(s => s.session_id === reco.winner_session_id)?.lic_titulo || "?"}
                  </div>
                  {reco.razones_principales?.length > 0 && (
                    <div className="text-[11px] mb-2">
                      <b>Razones:</b>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {reco.razones_principales.map((r, i) => <li key={`r-${i}`}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  {reco.riesgos_a_mitigar?.length > 0 && (
                    <div className="text-[11px] mb-2">
                      <b>Riesgos a mitigar:</b>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {reco.riesgos_a_mitigar.map((r, i) => <li key={`risk-${i}`}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  {reco.siguiente_paso_recomendado && (
                    <div className="text-[11px] text-amber-900 bg-amber-100 rounded p-2 mt-2">
                      <b>Siguiente paso:</b> {reco.siguiente_paso_recomendado}
                    </div>
                  )}
                </div>
              )}

              {ranking.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-[11px] font-bold text-gray-800 uppercase tracking-wide">📊 Ranking</div>
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-2 py-1 text-left">#</th>
                        <th className="px-2 py-1 text-left">Licitación</th>
                        <th className="px-2 py-1 text-center">Score</th>
                        <th className="px-2 py-1 text-center">Veredicto</th>
                        <th className="px-2 py-1 text-left">Justificación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.map((r, i) => {
                        const lic = sessions.find(s => s.session_id === r.session_id);
                        return (
                          <tr key={`rk-${i}`} className={`border-t border-gray-100 ${r.rank === 1 ? "bg-amber-50" : ""}`}>
                            <td className="px-2 py-1 font-bold">{r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : r.rank}</td>
                            <td className="px-2 py-1">{lic?.lic_titulo || r.titulo}</td>
                            <td className="px-2 py-1 text-center font-bold text-amber-700">{r.score_total}/10</td>
                            <td className="px-2 py-1 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                r.veredicto === "GO" ? "bg-emerald-100 text-emerald-800"
                                : r.veredicto === "NO-GO" ? "bg-rose-100 text-rose-800"
                                : "bg-amber-100 text-amber-800"
                              }`}>{r.veredicto}</span>
                            </td>
                            <td className="px-2 py-1 text-gray-700">{r.justificacion_corta}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-[11px] text-emerald-900 flex items-center gap-2">
                <CheckCircle2 size={14}/>
                Archivo de comparativa persistido en cada licitación implicada (categoría &quot;Comparativa Pando&quot;) · ${result.cost_usd} · {result.elapsed_s}s.
              </div>

              <details className="bg-gray-50 border border-gray-200 rounded-lg">
                <summary className="text-[11px] font-bold text-gray-700 px-3 py-2 cursor-pointer">📋 Ver markdown completo</summary>
                <pre className="text-[10px] text-gray-800 p-3 overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">{result.markdown}</pre>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
